import { isSensorOffline } from '../atoms-types/sensorFreshness';

export const FRIDGE_WARNING_F = 40;
export const FRIDGE_ALARM_F = 60;

export type HvacStatus = 'normal' | 'warning' | 'alarm' | 'offline';

export const HVAC_STATUS_COLORS: Record<HvacStatus, string> = {
  normal: '#2ecc71',
  warning: '#f1c40f',
  alarm: '#e74c3c', // solid red accents (alive but over threshold)
  offline: '#9e9e9e', // grey text/indicators; border uses SENSOR_OFFLINE_BORDER_COLOR
};

export const HVAC_STATUS_LABELS: Record<HvacStatus, string> = {
  normal: 'Normal',
  warning: 'Warning',
  alarm: 'Alarm',
  offline: 'Offline',
};

export type HvacThresholds = {
  warningF?: number | null;
  alarmF?: number | null;
};

const resolveWarningThreshold = (thresholds?: HvacThresholds): number => {
  const warning = thresholds?.warningF;
  return typeof warning === 'number' && Number.isFinite(warning) ? warning : FRIDGE_WARNING_F;
};

const resolveAlarmThreshold = (thresholds?: HvacThresholds): number => {
  const alarm = thresholds?.alarmF;
  return typeof alarm === 'number' && Number.isFinite(alarm) ? alarm : FRIDGE_ALARM_F;
};

export const getWarningThreshold = (thresholds?: HvacThresholds): number =>
  resolveWarningThreshold(thresholds);

export const getAlarmThreshold = (thresholds?: HvacThresholds): number =>
  resolveAlarmThreshold(thresholds);

// A unit is offline when it hasn't reported within the freshness window
// (pass lastRx) or when it has no current temperature reading. When lastRx is
// omitted, only the temperature is considered (back-compat for callers that
// don't have the timestamp).
export const getHvacStatus = (
  tempF: number | null | undefined,
  lastRx?: string | null,
  thresholds?: HvacThresholds,
): HvacStatus => {
  const warningThreshold = resolveWarningThreshold(thresholds);
  const alarmThreshold = resolveAlarmThreshold(thresholds);
  if (lastRx && isSensorOffline(lastRx)) return 'offline';
  if (tempF === null || tempF === undefined || Number.isNaN(tempF)) return 'offline';
  // Support both "too hot" and "too cold" alerting:
  // - alarm > warning  => high-side thresholds (temp above limits)
  // - alarm < warning  => low-side thresholds (temp below limits)
  const lowSide = alarmThreshold < warningThreshold;
  if (lowSide) {
    if (tempF < alarmThreshold) return 'alarm';
    if (tempF < warningThreshold) return 'warning';
    return 'normal';
  }
  if (tempF > alarmThreshold) return 'alarm';
  if (tempF > warningThreshold) return 'warning';
  return 'normal';
};

export const getDisplayLocation = (sensor: {
  Location?: string | null;
  deviceID?: string | null;
}) => {
  const loc = (sensor.Location || '').trim();
  if (loc && loc !== '---') return loc;
  const id = (sensor.deviceID || '').trim();
  if (id) return id;
  return '';
};

// HVAC page cards already imply context, so remove the redundant "hvac"
// token from per-card class labels.
export const getDisplayClass = (sensor: { Class?: string | null }) => {
  return String(sensor.Class || '')
    .replace(/\bhvac\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

export const isFreezerClass = (classLabel: string) =>
  /\bfreezer\b/i.test(classLabel);

export const isCoolerClass = (classLabel: string) =>
  /\b(fridge|cooler|reach[- ]?in|sandwich)\b/i.test(classLabel) ||
  (!isFreezerClass(classLabel) && !!classLabel);
